import { useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import type { ManualItineraryDeleteRequest, TripItineraryData } from 'tripPlanner/api/types'
import { computeCartPanelTotal, isCartPanelItemCheckoutReady, shouldShowReadyItemsOnlyNote } from '../utils/cartPanelUtils'
import { useCartPanelItems, type CartPanelItem } from './useCartPanelItems'
import { useCartPanelActions } from './useCartPanelActions'
import { useCartPanelReconciliation } from './useCartPanelReconciliation'

export type { CartPanelItem }

interface UseCartPanelArgs {
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  chatId: number | undefined
  isStreaming: boolean
  sendMessage: (message: string) => Promise<void>
  deleteManualItineraryTarget: (target: ManualItineraryDeleteRequest) => Promise<void>
  isLoggedIn: boolean
}

interface UseCartPanelReturn {
  items: Array<CartPanelItem>
  isLoading: boolean
  isError: boolean
  totalPrice: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  isLoggedIn: boolean
  handleToggleInclude: (item: CartPanelItem) => Promise<void>
  handleChangeRoom: (item: CartPanelItem) => void
  handleSetDates: () => void
  handleAdjustDays: (item: CartPanelItem) => void
  handleChooseTickets: (item: CartPanelItem) => void
  handleChooseDate: (item: CartPanelItem) => void
  handleChooseTime: (item: CartPanelItem) => void
  handleChooseDifferentTickets: (item: CartPanelItem) => void
  handleDelete: (item: CartPanelItem) => void
  handleCheckout: () => Promise<void>
  refetch: () => void
}

export function useCartPanel({
  multiCartId,
  itineraryData,
  chatId,
  isStreaming,
  sendMessage,
  deleteManualItineraryTarget,
  isLoggedIn,
}: UseCartPanelArgs): UseCartPanelReturn {
  const [optimisticExclusions, setOptimisticExclusions] = useState<Record<number, boolean>>({})
  const hasLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)

  const { items, isLoading, isError, refetch } = useCartPanelItems({
    multiCartId,
    itineraryData,
    optimisticExclusions,
  })

  const actions = useCartPanelActions({
    chatId,
    multiCartId,
    items,
    isStreaming,
    sendMessage,
    deleteManualItineraryTarget,
    setOptimisticExclusions,
  })

  useCartPanelReconciliation({ chatId, items, multiCartId })

  const { totalPrice, totalItemCount } = useMemo(
    () => computeCartPanelTotal(
      items.map(i => ({ cartExcluded: i.cartExcluded, price: i.pricing.price, memberPrice: i.pricing.memberPrice })),
      hasLuxPlusBenefits,
    ),
    [items, hasLuxPlusBenefits],
  )

  const hasCheckoutReadyItems = useMemo(
    () => items.some(isCartPanelItemCheckoutReady),
    [items],
  )

  const showReadyItemsOnlyNote = useMemo(
    () => shouldShowReadyItemsOnlyNote(items),
    [items],
  )

  return {
    items,
    isLoading,
    isError,
    totalPrice,
    totalItemCount,
    hasCheckoutReadyItems,
    showReadyItemsOnlyNote,
    isLoggedIn,
    ...actions,
    refetch,
  }
}
